import type { Game } from '@/types/game';

/**
 * Per-game feature detection for the Hub.
 *
 * The Hub renders game-specific sections only when the active game supports
 * them. Game ids are backend-defined (Laravel `games` table) and can drift
 * between staging and prod, so we match on the game NAME first (stable,
 * human-authored) and fall back to a known id.
 *
 * `CR_GAME_ID = 6` is the staging+prod Clash Royale id, previously hardcoded
 * in `src/routes/Tournaments.tsx`. Kept as a fallback for the rare case where
 * the name doesn't match the regex (renamed game variant, locale label, …).
 */
export const CR_GAME_ID = 6;

/**
 * Static Clash Royale fallback. Unlike the synthetic games below, CR IS a
 * real Laravel `games` row (id 6) — but a fresh user can reach the Hub
 * before `GET /games/available` resolves (or while the Laravel pipeline is
 * down, e.g. the unverified-email limbo). The public launch is CR-exclusive,
 * so `useGameSelection` injects this fallback whenever the backend list
 * doesn't (yet) contain id 6; the real row wins once it arrives.
 */
export const CR_GAME: Game = {
  id: CR_GAME_ID,
  name: 'Clash Royale',
  is_global: true,
  label: 'Clash Royale',
  required_template: 0,
  display_in_tournaments: true,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '',
  image: null,
  icon: '',
  top_banner: '',
  web_icon: '',
  platforms: [],
};

/**
 * Synthetic client-side game for QChess. Chess is NOT a Laravel `games` row
 * (it's a separate microservice), so `useGameSelection` injects this
 * pseudo-game into the picker when the chess feature flag is on — making it
 * selectable like Clash Royale / eFootball. The id is a large positive
 * sentinel that won't collide with backend ids and stays > 0 so the
 * localStorage rehydrate in `useGameSelection` accepts it. Selecting it lights
 * up the Hub's chess section via `featuresForGame()`.
 */
export const CHESS_GAME_ID = 999001;

export const CHESS_GAME: Game = {
  id: CHESS_GAME_ID,
  name: 'Chess',
  is_global: true,
  label: 'Chess',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/chess.svg',
  image: '/games/chess.svg',
  icon: '/games/chess.svg',
  top_banner: '/games/chess.svg',
  web_icon: '/games/chess.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QClans (Clash of Clans). Like QChess, CoC is
 * NOT a Laravel `games` row (separate microservice), so `useGameSelection`
 * injects this pseudo-game into the picker when the CoC feature flag is on —
 * making it selectable like Clash Royale / eFootball / Chess. Large positive
 * sentinel id that won't collide with backend ids and stays > 0 so the
 * localStorage rehydrate accepts it. Selecting it lights up the Hub's CoC
 * section via `featuresForGame()`.
 */
export const COC_GAME_ID = 999002;

export const COC_GAME: Game = {
  id: COC_GAME_ID,
  name: 'Clash of Clans',
  is_global: true,
  label: 'Clash of Clans',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/clash-of-clans.svg',
  image: '/games/clash-of-clans.svg',
  icon: '/games/clash-of-clans.svg',
  top_banner: '/games/clash-of-clans.svg',
  web_icon: '/games/clash-of-clans.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QGammon (Backgammon). Like QChess / QClans,
 * backgammon is NOT a Laravel `games` row (it's a separate first-party
 * microservice), so `useGameSelection` injects this pseudo-game into the picker
 * when the backgammon feature flag is on — making it selectable like the
 * others. Large positive sentinel id (won't collide with backend ids, stays > 0
 * so the localStorage rehydrate accepts it). Selecting it lights up the Hub's
 * backgammon section via `featuresForGame()`.
 */
export const BACKGAMMON_GAME_ID = 999003;

export const BACKGAMMON_GAME: Game = {
  id: BACKGAMMON_GAME_ID,
  name: 'Backgammon',
  is_global: true,
  label: 'Backgammon',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/backgammon.svg',
  image: '/games/backgammon.svg',
  icon: '/games/backgammon.svg',
  top_banner: '/games/backgammon.svg',
  web_icon: '/games/backgammon.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QChinesePoker (Pineapple OFC + Fantasyland).
 * Like QChess / QClans / QGammon, QChinesePoker is NOT a Laravel `games` row
 * (separate first-party microservice), so `useGameSelection` injects this
 * pseudo-game into the picker when the Chinese Poker feature flag is on —
 * making it selectable like the others. Large positive sentinel id (won't
 * collide with backend ids, stays > 0 so the localStorage rehydrate accepts
 * it). Selecting it lights up the Hub's Chinese Poker section via
 * `featuresForGame()`.
 */
export const CHINESE_POKER_GAME_ID = 999004;

export const CHINESE_POKER_GAME: Game = {
  id: CHINESE_POKER_GAME_ID,
  name: 'Chinese Poker',
  is_global: true,
  label: 'Chinese Poker',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/chinese-poker.svg',
  image: '/games/chinese-poker.svg',
  icon: '/games/chinese-poker.svg',
  top_banner: '/games/chinese-poker.svg',
  web_icon: '/games/chinese-poker.svg',
  platforms: [],
};

const CR_NAME_RE = /clash\s*royale/i;
const RIOT_NAME_RE =
  /league of legends|teamfight|\btft\b|legends of runeterra|valorant/i;
const CHESS_NAME_RE = /chess|scacchi/i;
// "Clash of Clans" only — must NOT match "Clash Royale" (handled by CR_NAME_RE).
const COC_NAME_RE = /clash\s*of\s*clans/i;
const BACKGAMMON_NAME_RE = /backgammon|tavola\s*reale/i;
const CHINESE_POKER_NAME_RE = /chinese\s*poker|pineapple\s*ofc|ofc\s*poker/i;

export interface GameFeatures {
  /** Clash Royale section (decks + battles + AI coach). */
  cr: boolean;
  /** Riot Games section (LoL / TFT / LoR / Valorant). */
  riot: boolean;
  /** QChess section (Lichess tournaments / duels / leaderboard). */
  chess: boolean;
  /** QClans section (Clash of Clans stats / war / competitions / coach). */
  coc: boolean;
  /** QGammon section (real-time backgammon 1v1 + ELO leaderboard). */
  backgammon: boolean;
  /** QChinesePoker section (heads-up Pineapple OFC + Fantasyland). */
  chinesePoker: boolean;
}

/**
 * Resolve which Hub feature sections a given game unlocks. Name-based match is
 * primary; the CR / Chess / CoC / Backgammon ids are fallbacks. Returns
 * all-false for `null` (no active game selected).
 */
export function featuresForGame(game: Game | null | undefined): GameFeatures {
  if (!game)
    return { cr: false, riot: false, chess: false, coc: false, backgammon: false, chinesePoker: false };
  const name = game.name ?? '';
  // CoC first so "Clash of Clans" never falls through to the CR matcher.
  const coc = COC_NAME_RE.test(name) || game.id === COC_GAME_ID;
  const cr = !coc && (CR_NAME_RE.test(name) || game.id === CR_GAME_ID);
  const riot = RIOT_NAME_RE.test(name);
  const chess = CHESS_NAME_RE.test(name) || game.id === CHESS_GAME_ID;
  const backgammon =
    BACKGAMMON_NAME_RE.test(name) || game.id === BACKGAMMON_GAME_ID;
  const chinesePoker =
    CHINESE_POKER_NAME_RE.test(name) || game.id === CHINESE_POKER_GAME_ID;
  return { cr, riot, chess, coc, backgammon, chinesePoker };
}
